import { computed, inject, onBeforeMount, ref, Ref } from 'vue'
import {
  FilterOverview,
  HiddenFilters,
  Settings,
  SettingsResponse,
} from '@/domain'
import { defaultClientColumns, filterDefinitions } from '@/data'
import { ServiceFactory } from '@/services/ServiceFactory'

const targetFilters = ['label', 'target']

const countVisibleFilters = (
  overview: FilterOverview,
  hiddenFilters: HiddenFilters,
  modules: Partial<Settings['modules']>,
  hideTarget: boolean,
  propertyIds: number[],
): number =>
  filterDefinitions[overview].filter(
    ({ key, module }) =>
      !hiddenFilters[overview].includes(key) &&
      (!module || Boolean(modules?.[module])) &&
      !(hideTarget && targetFilters.includes(key)),
  ).length +
  propertyIds.filter(
    (id) => !hiddenFilters[overview].includes(`property:${id}`),
  ).length

export const createFromSettingsResponse = (
  response: SettingsResponse,
): Settings => {
  const hiddenFilters: HiddenFilters = {
    clients: response?.hiddenFilters?.clients ?? [],
    locations: response?.hiddenFilters?.locations ?? [],
    interactions: response?.hiddenFilters?.interactions ?? [],
    issuedMaterials: response?.hiddenFilters?.issuedMaterials ?? [],
  }
  const modules = { ...response.modules }
  const hideTarget = response?.hideTarget || false
  const hideTargetFilter = response?.hideTargetFilter || false
  const customProperties = response?.customProperties || false
  const clientPropertyIds = customProperties
    ? (response?.clientPropertyIds ?? [])
    : []
  const locationPropertyIds = customProperties
    ? (response?.locationPropertyIds ?? [])
    : []

  return {
    general: {
      appName: response?.appName || '',
      lastImportDate: response?.lastImport?.date || null,
      defaultLanguage: response?.defaultLanguage || 'nl',
      multiLanguage: response?.multiLanguage || false,
      lockoutTime: response?.lockoutTime,
      sessionWarningTime: response?.sessionWarningTime,
      cyclesPerYear: response?.cyclesPerYear || 1,
      logoPath: response?.logoPath || null,
      apiVersion: response?.version ?? null,
    },
    clients: {
      editable: response?.editClients || false,
      fileImport: response?.clientFileImport || false,
      showNote: response?.clientShowNote || false,
      merge: response?.clientMerge === true,
      optIn: {
        isActive: response?.clientOptIn?.isActive || false,
        hasBypass: response?.clientOptIn?.hasBypass || false,
        askSignature: response?.clientOptIn?.askSignature || false,
        text: response?.clientOptIn?.text || '',
      },
    },
    customProperties: {
      isActive: customProperties,
    },
    therapeuticAreas: {
      isActive:
        (response?.therapeuticAreas ??
          (response?.modules && (response.modules as any).therapeuticAreas)) ||
        false,
    },
    locations: {
      editable: response?.editLocations || false,
      showNote: response?.locationNote || false,
      merge: response?.locationMerge === true,
    },
    issuedMaterials: {
      isActive: response?.issuedMaterials || false,
      limitPerCycle: response?.materialLimitPerCycle || 0,
      requireSignature: response?.materialRequireSignature || false,
      batchNumbers: response?.materialBatchNumbers || false,
    },
    interaction: {
      inputStrategy: response?.interactionInputStrategy || 'open',
      showNote: response?.interactionNote || false,
      showTime: response?.interactionTime || false,
    },
    clientOverview: {
      columns: response?.clientColumns ?? defaultClientColumns,
      hideTargetFilter: response?.hideTargetFilter || false,
    },
    locationOverview: {
      columns: response?.locationColumns || [],
    },
    interactionOverview: {
      columns: response?.interactionColumns || [],
    },
    hiddenFilters,
    filterCounts: {
      clients: countVisibleFilters(
        'clients',
        hiddenFilters,
        modules,
        false,
        clientPropertyIds,
      ),
      locations: countVisibleFilters(
        'locations',
        hiddenFilters,
        modules,
        false,
        locationPropertyIds,
      ),
      interactions: countVisibleFilters(
        'interactions',
        hiddenFilters,
        modules,
        hideTargetFilter,
        [],
      ),
      issuedMaterials: countVisibleFilters(
        'issuedMaterials',
        hiddenFilters,
        modules,
        false,
        [],
      ),
    },
    hideTarget,
    activity: {
      isActive: response?.activities || false,
      legend: response?.activityLegend || false,
      maxValue: response?.activityMaxValue || 3,
      filters: response?.activityFilters || false,
      switchView: response?.activitySwitchView || false,
      showDashboardWidgets: response?.activityShowDashboardWidgets || false,
    },
    // TODO: Remove this when the backend is updated
    modules,
  }
}

export class SettingsStore {
  private static settings: Settings | null = null

  public static async get(fresh = false) {
    if (this.settings === null || fresh) {
      await ServiceFactory.createSettingsService()
        .getAll()
        .then((response) => {
          this.settings = createFromSettingsResponse(response.data)
        })
    }

    return this.settings
  }
}

export const useSettings = (fresh = false) => {
  const isLoading = ref(true)
  const settings: Ref<Settings | null> = ref(null)
  // Not provided when useSettings() is called from App.vue itself (App is the
  // provider, and a component cannot inject its own provide) — refresh() guards
  // for null below.
  const updateNavigation = inject('updateNavigation', null) as
    (() => void) | null
  const refresh = async () => {
    isLoading.value = true
    settings.value = await SettingsStore.get(true)

    if (updateNavigation) {
      updateNavigation()
    }

    isLoading.value = false
  }
  const hasCustomProperties = computed(
    () => settings.value?.customProperties?.isActive ?? false,
  )
  const hasIssuedMaterials = computed(
    () => settings.value?.issuedMaterials?.isActive ?? false,
  )
  const hasTherapeuticAreas = computed(
    () => settings.value?.therapeuticAreas?.isActive ?? false,
  )
  const issuedMaterialSettings = computed(() => settings.value?.issuedMaterials)
  const requireSignatureForIssuedMaterials = computed(
    () => settings.value?.issuedMaterials?.requireSignature ?? false,
  )
  const lastImportDate = computed(() => settings.value?.general?.lastImportDate)
  const hasOptIn = computed(() => settings.value?.clients?.optIn?.isActive)
  const hasBypass = computed(() => settings.value?.clients?.optIn?.hasBypass)
  const askSignature = computed(
    () => settings.value?.clients?.optIn?.askSignature,
  )
  const appName = computed(() => settings.value?.general?.appName)
  const logoPath = computed(() => settings.value?.general?.logoPath ?? null)
  const defaultLanguage = computed(
    () => settings.value?.general?.defaultLanguage,
  )
  const inputStrategy = computed(
    () => settings.value?.interaction?.inputStrategy,
  )
  const apiVersion = computed(() => settings.value?.general?.apiVersion ?? null)
  const isFilterVisible = (overview: FilterOverview, filter: string) =>
    !(settings.value?.hiddenFilters?.[overview] ?? []).includes(filter)

  onBeforeMount(async () => {
    settings.value = await SettingsStore.get(fresh)
    isLoading.value = false
  })

  return {
    apiVersion,
    appName,
    defaultLanguage,
    inputStrategy,
    isFilterVisible,
    isLoading,
    logoPath,
    settings,
    hasCustomProperties,
    hasIssuedMaterials,
    hasTherapeuticAreas,
    issuedMaterialSettings,
    requireSignatureForIssuedMaterials,
    refresh,
    lastImportDate,
    hasOptIn,
    hasBypass,
    askSignature,
  }
}
